import { useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';

import {
  setPage,
  setLimit,
  setTotal,
  setColumnVisibility,
  setRaindropTableFilters,
  setClearRaindropTableFilters,
  setIsCancelRaindropModalOpen,
  setSelectedRaindropId,
} from '@Modal/reducers/raindrops/raindropTableSlice';
import { useSnackbar } from 'notistack';

// Types
import { StoreInterface } from 'src/app/types';
import { FilterItemType } from '@Modal/reducers/types';

// Graphql
import {
  useCancelRaindropCampaignMutation,
  RaindropCampaignsDocument,
} from '@Src/generated.graphql';

export const useRaindropsTable = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();
  const [cancelRaindropCampaign] = useCancelRaindropCampaignMutation();
  const {
    page,
    limit,
    total,
    selectedFilters,
    columnVisibility,
    raindropTableFilters,
    columnVisibilityOptions,
    isCancelRaindropModalOpen,
    selectedRaindropId,
  } = useSelector((state: StoreInterface) => state?.raindropTable);

  const raindropsListFilters = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    const newVisibleColumns = [...raindropsListFilters, ...column];
    if (raindropsListFilters.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = raindropsListFilters.filter((col) => col !== column[0]);
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const handleFilterSelect = (filter: FilterItemType) => {
    dispatch(setRaindropTableFilters(filter));
  };

  const handleClearRaindropTableFilters = () => {
    dispatch(setClearRaindropTableFilters());
  };

  const handleOpenCancelRaindropModal = (raindropId: string) => {
    dispatch(setSelectedRaindropId(raindropId));
    dispatch(setIsCancelRaindropModalOpen(true));
  };

  const handleCloseCancelRaindropModal = () => {
    dispatch(setIsCancelRaindropModalOpen(false));
    dispatch(setSelectedRaindropId(''));
  };

  const handleCancelRaindrop = async () => {
    try {
      await cancelRaindropCampaign({
        variables: {
          campaignId: selectedRaindropId,
        },
      });
      await apolloClient.refetchQueries({
        include: [RaindropCampaignsDocument],
      });
      dispatch(setIsCancelRaindropModalOpen(false));
      dispatch(setSelectedRaindropId(''));
      enqueueSnackbar('Raindrop has been successfully canceled', {
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    } catch (error) {
      enqueueSnackbar(error?.message, {
        autoHideDuration: 2000,
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // State
    limit,
    page,
    total,
    selectedFilters,
    columnVisibility,
    raindropsListFilters,
    raindropTableFilters,
    visiblityCustomizable,
    columnVisibilityOptions,
    isCancelRaindropModalOpen,
    // Actions
    setLimit,
    setPage,
    setTotal,
    setRaindropTableFilters,
    setClearRaindropTableFilters,
    // Handlers
    handleFilterSelect,
    handleCancelRaindrop,
    handleApplyColumnVisibility,
    handleOpenCancelRaindropModal,
    handleCloseCancelRaindropModal,
    handleClearRaindropTableFilters,
  };
};
